import AsyncStorage from '@react-native-async-storage/async-storage'
import type { BundleFloorConfig, UpdateBackoffRecord } from './bundleIdentity'

/**
 * ATL-1452 — what the bundle gate persists, with ONE storage-failure policy: nothing here ever throws,
 * and a read says whether storage could be READ at all — "unreadable" is never passed off as "absent"
 * (a back-off that could not be read must not be taken for no back-off: that would loop restarts).
 *
 *   • the update back-off (see UpdateBackoffRecord);
 *   • the last `config/app` floor fields the server sent — so the next launch can open at once when
 *     they name no hard floor this bundle is below (web has no Firestore cache to do that).
 *
 * AsyncStorage on every platform — on web that is localStorage, shared by every tab of the browser, and
 * deliberately so: a back-off says "this floor could not be reached from here" (nothing newer, the
 * reload landed on the same build, every try failed), which is as true for the next tab as for this
 * one. A per-tab record would make every new tab or PWA launch take its own forced reload.
 */

const BACKOFF_KEY = 'atlas.bundleLockout.backoff'
const PROBE_KEY = 'atlas.bundleLockout.probe'
const LAST_CONFIG_KEY = 'atlas.bundleLockout.lastConfig'

/** `readable: false` — storage could not be read. `value: null` — readable, and nothing (valid) there. */
export type StoredRead = { readable: true; value: unknown } | { readable: false }

const readJson = async (key: string): Promise<StoredRead> => {
  let raw: string | null
  try {
    raw = await AsyncStorage.getItem(key)
  } catch {
    return { readable: false }
  }
  try {
    return { readable: true, value: raw ? (JSON.parse(raw) as unknown) : null }
  } catch {
    return { readable: true, value: null } // garbage is no record
  }
}

/** The back-off record. */
export const readBackoffRecord = (): Promise<StoredRead> => readJson(BACKOFF_KEY)

/** One key, overwritten — nothing accumulates. */
export const writeBackoffRecord = async (record: UpdateBackoffRecord): Promise<boolean> => {
  try {
    await AsyncStorage.setItem(BACKOFF_KEY, JSON.stringify(record))
    return true
  } catch {
    return false
  }
}

/**
 * Take back a back-off a restart wrote and then did not use (called off, vetoed, or a reload that
 * threw) — ONLY if the key still holds exactly that record: a newer attempt (the floor was re-pointed)
 * may have written its own since, and erasing it would remove that attempt's loop guard.
 */
export const clearBackoffRecordIf = async (expected: UpdateBackoffRecord): Promise<boolean> => {
  const current = await readJson(BACKOFF_KEY)
  if (!current.readable) return false
  const v = current.value as Partial<UpdateBackoffRecord> | null
  if (!v || v.floor !== expected.floor || v.at !== expected.at || v.reason !== expected.reason) return true
  try {
    await AsyncStorage.removeItem(BACKOFF_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * Whether a back-off could be written HERE, now: a write read back, then removed. A lock is only taken
 * when this is true — a lock whose restart must be vetoed for want of its back-off (see
 * BundleUpdateScreen) would only flash the lockout on every launch, and restart nothing.
 */
export const probeBackoffStorage = async (): Promise<boolean> => {
  try {
    const token = String(Date.now())
    await AsyncStorage.setItem(PROBE_KEY, token)
    const readBack = await AsyncStorage.getItem(PROBE_KEY)
    await AsyncStorage.removeItem(PROBE_KEY)
    return readBack === token
  } catch {
    return false
  }
}

/** The last floor fields the server sent (persistableFloorConfig), or null if none was ever stored. */
export const readLastFloorConfig = (): Promise<StoredRead> => readJson(LAST_CONFIG_KEY)

export const writeLastFloorConfig = async (config: BundleFloorConfig): Promise<boolean> => {
  try {
    await AsyncStorage.setItem(LAST_CONFIG_KEY, JSON.stringify(config))
    return true
  } catch {
    return false
  }
}
